import { AccessibilityInfo, Platform } from 'react-native'
import { useEffect, useState } from 'react'

/**
 * Reads the system contrast preference once and keeps it live.
 *
 * Liquid surfaces are decorative only. When a person asks for reduced
 * transparency, every consumer replaces the material with an opaque semantic
 * surface instead of silently leaving low-contrast content behind.
 */
export function useReducedTransparency(): boolean {
  const [reducedTransparency, setReducedTransparency] = useState(false)

  useEffect(() => {
    // React Native Web exposes the AccessibilityInfo namespace but does not
    // implement iOS's transparency API. Calling the typed-but-missing method
    // turns every GlassSurface into a fatal overlay in browsers. Web already
    // has its bounded CSS fallback, so its accessible default is the normal
    // material path; native capability checks remain live below.
    if (Platform.OS === 'web') return undefined

    const transparencyApi = AccessibilityInfo as typeof AccessibilityInfo & {
      isReduceTransparencyEnabled?: () => Promise<boolean>
      addEventListener?: (
        eventName: 'reduceTransparencyChanged',
        listener: (enabled: boolean) => void,
      ) => { remove: () => void }
    }
    if (typeof transparencyApi.isReduceTransparencyEnabled !== 'function') return undefined

    let mounted = true
    void transparencyApi
      .isReduceTransparencyEnabled()
      .then((enabled) => {
        if (mounted) setReducedTransparency(enabled)
      })
      // A system accessibility query must never prevent the screen itself
      // from rendering. The normal material is the explicit safe default when
      // a device declines to provide this optional preference.
      .catch(() => {
        if (mounted) setReducedTransparency(false)
      })
    const subscription = transparencyApi.addEventListener?.(
      'reduceTransparencyChanged',
      setReducedTransparency,
    )

    return () => {
      mounted = false
      subscription?.remove()
    }
  }, [])

  return reducedTransparency
}
